/**
 * 判断响应体是否像 nginx/HTML 错误页（用于区分 Nitro JSON 与误配 host 的 404）。
 */
export function isLikelyHtmlErrorBody(text: string): boolean {
  const t = text.trimStart()
  return t.startsWith('<!') || t.startsWith('<html') || t.startsWith('<HTML')
}

export type MarketingProxyJsonErrorCopy = {
  /** 非 JSON 或 HTML 错误页 */
  nonJson: string
  /** 404 且为 HTML（多为未命中 Nitro `/api`） */
  html404: string
  /** 其它 HTTP 错误 */
  httpError: (status: number) => string
}

/**
 * 将营销站 Nitro 代理的 `fetch` 响应解析为 JSON；对 HTML/404 给出可排障的中文说明。
 */
export async function readJsonFromMarketingProxyResponse<T = unknown>(
  res: Response,
  copy: MarketingProxyJsonErrorCopy,
): Promise<T> {
  const contentType = res.headers.get('content-type') || ''
  const text = await res.text()

  const tryParseJson = (): T | null => {
    try {
      return JSON.parse(text) as T
    } catch {
      return null
    }
  }

  if (!res.ok) {
    if (res.status === 404 && isLikelyHtmlErrorBody(text)) {
      throw new Error(copy.html404)
    }
    if (isLikelyHtmlErrorBody(text)) {
      throw new Error(copy.nonJson)
    }
    const parsed = tryParseJson()
    if (parsed !== null && typeof parsed === 'object' && parsed !== null && 'message' in parsed) {
      throw new Error(String((parsed as { message?: unknown }).message || copy.httpError(res.status)))
    }
    throw new Error(copy.httpError(res.status))
  }

  if (!contentType.includes('application/json') && !contentType.includes('text/json') && text.length > 0) {
    if (isLikelyHtmlErrorBody(text)) {
      throw new Error(copy.nonJson)
    }
  }

  const parsed = tryParseJson()
  if (parsed === null) {
    throw new Error(copy.nonJson)
  }
  return parsed
}
